import { FormProvider, useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import { LBtn } from "../../../../../../common/components/LBtn";
import { LInputField } from "../../../../../../common/components/LInputField";
import { useApi } from "../../../../../../services/useApi";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { LocationsSkeleton } from "../../../../../../common/helpers/skeletons";
import { LModal } from "../../../../../../common/components/LModal";
import { AddLocationHeader } from "./AddLocationHeader";
import { LocationCard } from "./LocationCard";
import { AddLocationModal } from "./AddLocationModal";
import { OrgLocation, updateLocations } from "@store/locationsSlice";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { MissingRolesModal } from "@fadmin/components/missingRolesModal";
import { OrganizationUsers } from "@store/generalTypes";

export const AccountLocations = (): JSX.Element => {
    const validateMethods = useForm();

    const dispatch = useAppDispatch();
    const { loading, getOrganizationUsers, getOrganizationLocations } = useApi();
    const { organization, locations, currentLocation, organizationAssessments } = useAppSelector((x) => {
        return {
            organization: x.organization,
            locations: x.orgLocations.locations,
            currentLocation: x.orgLocations.currentLocation,
            organizationAssessments: x.organizationAssessments
        };
    });
    const { isClinicalAdmin } = useUserPermission({});

    const [staffUsers, setStaffUsers] = useState<{ [key: number]: number }>({});
    const [organizationUsers, setOrganizationUsers] = useState<OrganizationUsers>();
    const [hasMissingRoles, setHasMissingRoles] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState("");
    const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
    const [locationEdited, setLocationEdited] = useState<OrgLocation>();

    useEffect(() => {
        void loadOrganizationUsers();
    }, [organization, locations]);

    useEffect(() => {
        if (organizationUsers) {
            setShowModal(true);
        }
    }, [organizationUsers]);

    const loadOrganizationUsers = async (): Promise<void> => {
        const locationsIds = locations.map((l) => l.id);

        const users = await getOrganizationUsers(organization.id!, locationsIds);

        const locationToUsers: { [key: number]: number } = {};

        users.items.forEach((user) => {
            user.locations?.forEach((location) => {
                locationToUsers[location.id] = (locationToUsers[location.id] ?? 0) + 1;
            });
        });

        setOrganizationUsers(users);
        setStaffUsers(locationToUsers);
    };

    const handleOnLocationRefresh = useCallback(async () => {
        const orgLocations = await getOrganizationLocations(organization.id!);

        dispatch(updateLocations(orgLocations));

        void loadOrganizationUsers();
    }, [organization.id]);

    const toggleLocationModal = (): void => {
        if (addLocationModalOpen) {
            setLocationEdited(undefined);
        }

        setAddLocationModalOpen(!addLocationModalOpen);
    };

    const handleEditLocation = (loc: OrgLocation): void => {
        setLocationEdited(loc);

        setAddLocationModalOpen(true);
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setSearch(e.target.value.toLocaleLowerCase());
    };

    const getNextLocationId = (): number => {
        if (locations && isEmpty(locations)) {
            return 1;
        }

        return (
            Number(
                locations
                    .map((l) => l.displayId)
                    .sort((a, b) => b.localeCompare(a))?.[0]
                    .slice(-1)
            ) + 1
        );
    };

    const filteredLocations = locations.filter((location) => location.name.toLocaleLowerCase().includes(search));

    const locationCount = filteredLocations.length;

    if (loading) {
        return <LocationsSkeleton />;
    }

    return (
        <>
            <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center mt-6">
                <h1 className="is-size-5 has-text-ldark-gray">Locations</h1>
                {isClinicalAdmin && (
                    <LBtn
                        variation="unstyled"
                        disabled={!organizationAssessments.hasPurchases}
                        onClick={toggleLocationModal}
                    >
                        <p className="has-text-weight-semibold">+ Add a location</p>
                    </LBtn>
                )}
            </div>
            <div className="divider" />

            {showModal && (
                <MissingRolesModal
                    organizationUsers={organizationUsers}
                    onHasMissingRole={(hasMissing) => setHasMissingRoles(hasMissing)}
                />
            )}

            <FormProvider {...validateMethods}>
                <div className="is-flex is-flex-direction-row is-align-items-center">
                    <div className="has-text-weight-normal">{`${locationCount} location${
                        locationCount === 1 ? "" : "s"
                    }`}</div>
                    <div className="column is-one-fifth ml-6">
                        <LInputField
                            inputName="searchTable"
                            placeholder="Search locations"
                            onChange={handleSearch}
                            isRounded
                        />
                    </div>
                </div>
                {!filteredLocations?.length ? (
                    <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-center">
                        No locations found.
                    </div>
                ) : (
                    filteredLocations.map((location, idx) => (
                        <LocationCard
                            key={`${location.id}-${idx}`}
                            data={location}
                            staffUsers={staffUsers[location.id] ?? 0}
                            highlight={location.id === currentLocation?.id && hasMissingRoles}
                            onEdit={() => handleEditLocation(location)}
                            disableButton={!organizationAssessments.hasPurchases}
                        />
                    ))
                )}
            </FormProvider>
            {addLocationModalOpen && (
                <LModal title={<AddLocationHeader />} onClose={toggleLocationModal} width="s">
                    <AddLocationModal
                        nextLocationId={getNextLocationId()}
                        editedLocation={locationEdited}
                        refresh={handleOnLocationRefresh}
                    />
                </LModal>
            )}
        </>
    );
};
